import { fetchApi } from "common/utils/Api";
import type { FeedBudgetPlan } from "features/feedBudgetPlan/types";

export interface FeedBudgetPlanListItem {
  guid: string;
  farmGuid: string;
  name: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface FeedBudgetPlanWithVersion extends FeedBudgetPlan {
  version: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  animalGroupBudgets: (FeedBudgetPlan["animalGroupBudgets"][number] & {
    guid: string;
    sortOrder?: number;
  })[];
}

export async function listFeedBudgetPlans(
  farmGuid: string
): Promise<FeedBudgetPlanListItem[]> {
  return fetchApi(
    `feedBudgetPlans?farmGuid=${encodeURIComponent(farmGuid)}`
  ) as Promise<FeedBudgetPlanListItem[]>;
}

export async function getFeedBudgetPlan(
  guid: string
): Promise<FeedBudgetPlanWithVersion> {
  return fetchApi(
    `feedBudgetPlans/${guid}`
  ) as Promise<FeedBudgetPlanWithVersion>;
}

export async function createFeedBudgetPlan(body: {
  farmGuid: string;
  name: string;
  startDate: string;
  endDate: string;
  initialPastureCoverKgHa: number;
  targetResidualKgHa: number;
  grazingMonths: FeedBudgetPlan["grazingMonths"];
  supplementSubstitutionRates: FeedBudgetPlan["supplementSubstitutionRates"];
  animalGroupBudgets: FeedBudgetPlanWithVersion["animalGroupBudgets"];
}): Promise<FeedBudgetPlanWithVersion> {
  return fetchApi("feedBudgetPlans", {
    method: "POST",
    body: JSON.stringify({
      ...body,
      animalGroupBudgets: body.animalGroupBudgets.map(
        ({ guid: _g, ...rest }) => rest
      ),
    }),
  }) as Promise<FeedBudgetPlanWithVersion>;
}

export async function updateFeedBudgetPlan(
  guid: string,
  body: {
    name: string;
    startDate: string;
    endDate: string;
    initialPastureCoverKgHa: number;
    targetResidualKgHa: number;
    grazingMonths: FeedBudgetPlan["grazingMonths"];
    supplementSubstitutionRates: FeedBudgetPlan["supplementSubstitutionRates"];
    version: number;
  }
): Promise<FeedBudgetPlanWithVersion> {
  return fetchApi(`feedBudgetPlans/${guid}`, {
    method: "PUT",
    body: JSON.stringify(body),
  }) as Promise<FeedBudgetPlanWithVersion>;
}

export async function updateFeedBudgetPlanAnimalGroupBudget(
  budgetGuid: string,
  body: {
    animalGroupName: string;
    grazingAreaHa: number;
    sortOrder?: number;
    monthlyGrazingAreaHa?: Record<string, number>;
    feedTypeUtilisation?: Record<string, number>;
    fieldFeedingUtilisationPercent?: number;
    stockClassPlans: FeedBudgetPlanWithVersion["animalGroupBudgets"][number]["stockClassPlans"];
    supplementFeedTypes: FeedBudgetPlanWithVersion["animalGroupBudgets"][number]["supplementFeedTypes"];
    supplementPlans: FeedBudgetPlanWithVersion["animalGroupBudgets"][number]["supplementPlans"];
    version: number;
  }
): Promise<{ guid: string }> {
  return fetchApi(`feedBudgetPlans/animalGroupBudgets/${budgetGuid}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  }) as Promise<{ guid: string }>;
}

export async function activateFeedBudgetPlan(
  guid: string
): Promise<{ guid: string; isActive: boolean }> {
  return fetchApi(`feedBudgetPlans/${guid}/activate`, {
    method: "PATCH",
  }) as Promise<{ guid: string; isActive: boolean }>;
}

export async function deleteFeedBudgetPlan(guid: string): Promise<void> {
  await fetchApi(`feedBudgetPlans/${guid}`, { method: "DELETE" });
}
